import {type LineItem, type PaymentRequestOptions} from '@square/web-payments-sdk-types';
import {type PreparedCartTotalItem, type CartItem} from '@woocommerce/types';
import {formatAmount} from './currency';

type CorrectedPreparedCartTotalItem = PreparedCartTotalItem & {key?: string; valueWithTax: number};

type WooCommerceBlocksData = {
	merchantCountry: string;
	currency: string;
	items: CartItem[];
	summary: PreparedCartTotalItem[];
	total: PreparedCartTotalItem;
	displayPricesIncludingTax: boolean;
};

function buildOptions(options: WooCommerceBlocksData): PaymentRequestOptions {
	return {
		countryCode: options.merchantCountry,
		currencyCode: options.currency,
		lineItems: lineItems(options),
		discounts: discounts(options),
		taxLineItems: taxLineItems(options),
		shippingLineItems: shippingLineItems(options),
		total: total(options),
		requestBillingContact: false,
		requestShippingContact: false,
	};
}

function lineItems(options: WooCommerceBlocksData): LineItem[] {
	const lineItems = new Array<LineItem>();

	for (const cartItem of options.items) {
		lineItems.push({
			id: cartItem.sku,
			label: cartItem.name + ' × ' + cartItem.quantity,
			amount: formatAmount(
				cartItem.totals.line_subtotal,
				cartItem.totals.line_subtotal_tax,
				cartItem.totals.currency_code,
				options.displayPricesIncludingTax,
			),
		});
	}

	return lineItems;
}

function discounts(options: WooCommerceBlocksData): LineItem[] {
	const discounts = new Array<LineItem>();

	for (const lineTotal of options.summary as CorrectedPreparedCartTotalItem[]) {
		if (lineTotal['key'] !== 'total_discount') {
			continue;
		}

		if (lineTotal.value === 0) {
			continue;
		}

		const amount = String(options.displayPricesIncludingTax ? lineTotal.valueWithTax : lineTotal.value);
		const tax = '0';

		discounts.push({
			label: lineTotal.label,
			amount: formatAmount(amount, tax, options.currency),
		});
	}

	return discounts;
}

function taxLineItems(options: WooCommerceBlocksData): LineItem[] {
	const taxLineItems = new Array<LineItem>();

	for (const lineTotal of options.summary as CorrectedPreparedCartTotalItem[]) {
		if (lineTotal['key'] !== 'total_tax') {
			continue;
		}

		if (lineTotal.value === 0) {
			continue;
		}

		taxLineItems.push({
			label: lineTotal.label,
			amount: formatAmount(String(lineTotal.value), '0', options.currency),
		});
	}

	return taxLineItems;
}

function shippingLineItems(options: WooCommerceBlocksData): LineItem[] {
	const shippingLineItems = new Array<LineItem>();

	for (const lineTotal of options.summary as CorrectedPreparedCartTotalItem[]) {
		if (lineTotal['key'] !== 'total_shipping') {
			continue;
		}

		if (lineTotal.value === 0) {
			continue;
		}

		shippingLineItems.push({
			label: lineTotal.label,
			amount: formatAmount(String(lineTotal.value), '0', options.currency),
		});
	}

	return shippingLineItems;
}

function total(options: WooCommerceBlocksData): LineItem {
	return {
		label: options.total.label,
		amount: formatAmount(String(options.total.value), '0', options.currency),
	};
}

export {
	buildOptions,
};
